// Copyright (c) 2025. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import {
  collection,
  query,
  where,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  collectionGroup,
  addDoc,
  setDoc,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';

import { OBSERVATIONS_COLLECTION } from './FirebaseObservationUtils';
import { BALE_DOCUMENT_PATH } from './FirebaseUtils';

import { LABELSET_SETTINGS_DOCUMENT_PATH } from './FirebaseUtils';
import { t } from 'i18next';

const ACTIVE_GLOBAL_LABELSETS_DOC = 'labelsets/global_labelsets';
const GLOBAL_LABELSETS_COLLECTION = 'labelsets/global_labelsets/global_labelsets';
const LABELSET_PROFILES_COLLECTION = 'labelsets/labelset_profiles/labelset_profiles';
const LABELING_TASKS_COLLECTION = (clientId) => `clients/${clientId}/labeling_tasks`;
const LABELING_TASKS_COLLECTION_GROUP = 'labeling_tasks';
const COLORS_SETTINGS_DOC = 'settings/colors_settings';

export const LABELING_TASK_STATUSES = ['in_progress', 'stopped', 'completed', 'archived'];

/**
 * Get the ids of the active global labelsets
 * @param {Firestore} firestore - The firestore instance
 * @returns {Object} - The active global labelset ids
 */
export async function getActiveGlobalLabelsetData(firestore) {
  const activeGlobalLabelsetsRef = doc(firestore, ACTIVE_GLOBAL_LABELSETS_DOC);
  const activeGlobalLabelsetsSnap = await getDoc(activeGlobalLabelsetsRef);
  return activeGlobalLabelsetsSnap.data();
}

/**
 * Subscribe to labelset settings (labelsets/labelset_settings).
 * @param {Firestore} firestore - The Firestore instance
 * @param {Function} callback - Called with the settings data whenever it changes
 * @returns {Function} Unsubscribe function
 */
export function subscribeToLabelsetSettings(firestore, callback) {
  return onSnapshot(doc(firestore, LABELSET_SETTINGS_DOCUMENT_PATH), (snapshot) => {
    callback(snapshot.exists() ? snapshot.data() : null);
  });
}

/**
 * Get the active global labelsets
 * @param {Firestore} firestore - The firestore instance
 * @returns {Object} - The active global labelsets
 */
export async function getActiveGlobalLabelsets(firestore) {
  const activeGlobalLabelsetData = await getActiveGlobalLabelsetData(firestore);
  const ids = Object.values(activeGlobalLabelsetData?.active_labelsets || {});
  if (ids.length === 0) return {};

  const labelsetResults = await Promise.all(ids.map((labelsetId) => getGlobalLabelsetById(firestore, labelsetId)));

  const activeGlobalLabelsets = ids.reduce((acc, labelsetId, index) => {
    const labelset_id = labelsetResults[index]?.labelset;
    acc[labelset_id] = labelsetResults[index];
    return acc;
  }, {});

  return activeGlobalLabelsets;
}

/**
 * Construct labelset groups from the labelsets and active global labelset data
 * Will fetch the labelsets and active global labelset data from the database if not provided
 * @param {Firestore} firestore - The firestore instance
 * @param {Object} labelsets - The labelsets data
 * @param {Object} activeGlobalLabelsetData - The active global labelset data (containing metadata about labelset groups)
 * @returns {Object} - The labelset groups
 */
export async function constructLabelsetGroups(firestore, labelsets, activeGlobalLabelsetData = null) {
  // If no activeGlobalLabelsetData provided, fetch it directly from the database
  if (!activeGlobalLabelsetData) {
    activeGlobalLabelsetData = await getActiveGlobalLabelsetData(firestore);
  }

  // Construct labelset groups
  const labelsetGroups = {};
  Object.entries(activeGlobalLabelsetData?.groups || {}).forEach(([groupId, displayName]) => {
    labelsetGroups[groupId] = {
      labelset: groupId,
      display_name: displayName,
      labels: {},
    };
  });

  Object.values(labelsets).forEach((labelset) => {
    if (!labelset.group) return;
    if (!labelsetGroups[labelset.group]) return;
    labelsetGroups[labelset.group].type = labelset.type;
    labelsetGroups[labelset.group].labels = {
      ...labelsetGroups[labelset.group].labels,
      ...labelset.labels,
    };
  });

  return labelsetGroups;
}

/**
 * Merge each entry's `labels` with the client-specific `labels` from `defaultMap` (default
 * labels override matching global ones; only `labels` changes). Non-mutating.
 *
 * @param {Object} globalMap - The global labelsets (or labelset groups), keyed by name
 * @param {Object} defaultMap - The client's default labelsets (or groups), keyed by name
 * @returns {Object} - A new map with client-specific labels merged in where available
 */
export function applyDefaultLabels(globalMap = {}, defaultMap = {}) {
  const result = {};
  for (const [key, globalEntry] of Object.entries(globalMap)) {
    const defaultLabels = defaultMap?.[key]?.labels;
    // Pass through when there's nothing to merge or the entry is a non-object (e.g. a null labelset).
    if (!defaultLabels || !globalEntry || typeof globalEntry !== 'object') {
      result[key] = globalEntry;
      continue;
    }
    result[key] = { ...globalEntry, labels: { ...globalEntry.labels, ...defaultLabels } };
  }
  return result;
}

/**
 * Get the `material_class_colors` map from `settings/colors_settings`, keyed by color key
 * (e.g. `blue`, `other`). Resolves silently to `{}` if the doc is missing or the read fails.
 *
 * @param {Firestore} firestore - The firestore instance
 * @returns {Object} - The material class colors map, or `{}` if unavailable
 */
export async function getMaterialClassColors(firestore) {
  try {
    const colorsSettingsRef = doc(firestore, COLORS_SETTINGS_DOC);
    const colorsSettingsSnap = await getDoc(colorsSettingsRef);
    return colorsSettingsSnap.exists() ? colorsSettingsSnap.data()?.material_class_colors || {} : {};
  } catch (error) {
    console.error('Failed to fetch material class colors', error);
    return {};
  }
}

/**
 * Resolve each label's `display_color` key (e.g. `"blue"`) to its color object from
 * `materialClassColors`, falling back to `other`. Labels stay untouched when nothing resolves
 * (so an empty map leaves them as-is). Only `labels` changes; non-mutating.
 *
 * @param {Object} globalMap - The labelsets (or labelset groups), keyed by name
 * @param {Object} materialClassColors - The material class colors map, keyed by color key
 * @returns {Object} - A new map with each label's `display_color` resolved to a color object
 */
export function applyDisplayColors(globalMap = {}, materialClassColors = {}) {
  const result = {};
  for (const [key, entry] of Object.entries(globalMap)) {
    const labels = entry?.labels;
    if (!labels || typeof labels !== 'object') {
      result[key] = entry;
      continue;
    }

    let changed = false;
    const nextLabels = {};
    for (const [labelKey, label] of Object.entries(labels)) {
      const colorKey = label?.display_color;
      const color =
        typeof colorKey === 'string' ? (materialClassColors[colorKey] ?? materialClassColors.other) : undefined;
      if (color !== undefined) {
        nextLabels[labelKey] = { ...label, display_color: color };
        changed = true;
      } else {
        nextLabels[labelKey] = label;
      }
    }

    result[key] = changed ? { ...entry, labels: nextLabels } : entry;
  }
  return result;
}

/**
 * Get a specific global labelset by its ID
 * @param {Firestore} firestore - The firestore instance
 * @param {string} labelsetId - The ID of the labelset to retrieve
 * @returns {Object|null} - The labelset data or null if not found
 */
export async function getGlobalLabelsetById(firestore, labelsetId) {
  const labelsetRef = doc(firestore, GLOBAL_LABELSETS_COLLECTION, labelsetId);
  const labelsetSnap = await getDoc(labelsetRef);

  if (labelsetSnap.exists()) {
    return {
      id: labelsetSnap.id,
      ...labelsetSnap.data(),
    };
  } else {
    return null;
  }
}

/**
 * Get all labelset profiles
 * @param {Firestore} firestore - The firestore instance
 * @returns {Object} - The labelset profiles data
 */
export async function getLabelsetProfiles(firestore) {
  const labelsetProfiles = {};
  const labelsetProfilesRef = collection(firestore, LABELSET_PROFILES_COLLECTION);
  const labelsetProfilesSnap = await getDocs(labelsetProfilesRef);
  labelsetProfilesSnap.forEach((doc) => {
    labelsetProfiles[doc.id] = doc.data();
  });
  return labelsetProfiles;
}

/**
 * Get a specific labelset profile by its ID
 * @param {Firestore} firestore - The firestore instance
 * @param {string} labelsetProfileId - The id of the labelset profile to retrieve
 * @returns {Object|null} - The labelset profile data or null if not found
 */
export async function getLabelsetProfileById(firestore, labelsetProfileId) {
  const labelsetProfileRef = doc(firestore, LABELSET_PROFILES_COLLECTION, labelsetProfileId);
  const labelsetProfileSnap = await getDoc(labelsetProfileRef);

  if (labelsetProfileSnap.exists()) {
    return {
      id: labelsetProfileSnap.id,
      ...labelsetProfileSnap.data(),
    };
  } else {
    return null;
  }
}

/**
 * Get a labelset profile by its role for the client. The default role is "default".
 * @param {Firestore} firestore - The firestore instance
 * @param {Object} clientData - The client data
 * @param {string} role - The role of the labelset profile for the client. The default role is "default".
 * @returns {Object|null} - The labelset profile data or null if not found
 */
export async function getLabelsetProfileByClientRole(firestore, clientData, role = 'default') {
  const labelsetProfileId = (clientData?.labelset_profiles || {})?.[role];
  if (!labelsetProfileId) {
    console.error(`No labelset profile found for role ${role}`);
    return null;
  }

  return getLabelsetProfileById(firestore, labelsetProfileId);
}

/**
 * Get the display name of a labelset
 * @param {Object} labelset - The labelset
 * @param {string} languageCode - The language code
 * @returns {string} - The display name of the labelset
 */
export function getLabelsetDisplayName(labelset, languageCode = 'en') {
  const labelsetDisplayNames = labelset?.display_name || {};
  if (languageCode in labelsetDisplayNames) {
    return labelsetDisplayNames[languageCode];
  } else if ('en' in labelsetDisplayNames) {
    return labelsetDisplayNames['en'];
  } else {
    return labelset;
  }
}

/**
 * Group a flat map of labelset profile documents into version bundles.
 *
 * Labelset profiles are versioned by creating a new document per version, all sharing the same
 * `profile_id` field (see labelset_management). Newest version = highest `version`. This helper
 * bundles those documents so a UI can present one entry per logical profile plus a version picker.
 *
 * @param {Object} profilesByDocId - Map of {docId: profileData}, as returned by getLabelsetProfiles
 * @param {string} languageCode - Language code used to build the display label
 * @returns {{ groups: Array, byDocId: Object }} groups sorted by label (asc), each with versions
 *   sorted by version (desc); byDocId maps every docId back to its group's profileId.
 */
export function groupLabelsetProfilesByVersion(profilesByDocId = {}, languageCode = 'en') {
  const groupsById = {};
  const byDocId = {};

  Object.entries(profilesByDocId).forEach(([docId, data]) => {
    const profileId = data?.profile_id ?? docId;
    const version = Number.isFinite(data?.version) ? data.version : 0;

    if (!groupsById[profileId]) {
      groupsById[profileId] = { profileId, versions: [] };
    }
    groupsById[profileId].versions.push({ docId, version, displayName: data?.display_name });
    byDocId[docId] = { profileId };
  });

  Object.values(groupsById).forEach((group) => {
    // Newest version first.
    group.versions.sort((a, b) => b.version - a.version);
    group.versions.forEach((v, index) => {
      v.isLatest = index === 0;
    });
    // Label uses the newest version's display name, always disambiguated by profile_id.
    const newestName = getLabelsetDisplayName({ display_name: group.versions[0].displayName }, languageCode);
    const resolvedName = typeof newestName === 'string' ? newestName : group.profileId;
    group.label = `${resolvedName} (${group.profileId})`;
  });

  const groups = Object.values(groupsById).sort((a, b) => a.label.localeCompare(b.label));

  return { groups, byDocId };
}

/**
 * Get the display name of a label
 * @param {Object} labelset - The labelset
 * @param {string} label - The label
 * @param {string} languageCode - The language code
 * @returns {string} - The display name of the label
 */
export function getLabelDisplayName(labelset, label, languageCode = 'en') {
  if (label === '__unknown__') return t('unknown_label');
  const labelDisplayNames = labelset?.labels?.[label]?.display_name || {};
  if (languageCode in labelDisplayNames) {
    return labelDisplayNames[languageCode];
  } else if ('en' in labelDisplayNames) {
    return labelDisplayNames['en'];
  } else {
    return label;
  }
}

/**
 * Build a converter bound to a single labelset. Centralizes all knowledge of
 * how that labelset's unit (`labelset.labels.unit`) shapes the value between
 * storage form and display form. Callers bind once per labelset, then use
 * `toDisplay` / `toStorage` / `format` / `displayUnit` rather than reimplementing
 * the ratio rule at each call site.
 *
 * Storage convention: ratio values are persisted in `[0, 1]`. Other units are
 * persisted in their natural scale (e.g. kg in kg). The converter swaps
 * `'ratio'` for `'%'` on the way out and applies a ×100 / ÷100 transform for
 * numeric ratio values; non-ratio numerics and non-numeric types pass through.
 *
 * @param {Object} labelset
 * @returns {{
 *   type: string|undefined,
 *   isRatio: boolean,
 *   rawUnit: string|undefined,
 *   displayUnit: string|undefined,
 *   toDisplay: (value:*) => *,
 *   toStorage: (value:*) => *,
 *   format: (value:*, languageCode?:string, decimals?:number) => string,
 * }}
 */
export function labelsetValueConverter(labelset) {
  const type = labelset?.type;
  const rawUnit = labelset?.labels?.unit;
  const isRatio = rawUnit === 'ratio';
  const displayUnit = isRatio ? '%' : rawUnit;

  function toDisplay(value) {
    if (type === 'number' && isRatio && typeof value === 'number') {
      // Strip IEEE-754 representation noise from the multiplication
      // (e.g. 0.55 * 100 → 55.00000000000001). Rounding to 10 decimals is
      // well past the typical noise threshold but doesn't truncate real precision.
      return Math.round(value * 100 * 1e10) / 1e10;
    }
    return value;
  }

  function toStorage(value) {
    // Single conversion rule for both form save paths and sliders. Permissive:
    // callers can pass strings (from inputs), empties, or invalid values. Non-
    // numeric labelsets pass through untouched. Ratio values are always treated
    // as display form (percentages) and stored ÷100 (e.g. 25 → 0.25, 0.5 → 0.005).
    if (type !== 'number') return value;
    if (value === '' || value === null || value === undefined) return value;
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    if (!Number.isFinite(parsed)) return value;
    if (!isRatio) return parsed;
    // Strip IEEE-754 noise from the division (e.g. 2.93 / 100 →
    // 0.029300000000000003) so storage holds clean values — mirrors the
    // rounding in toDisplay.
    return Math.round((parsed / 100) * 1e10) / 1e10;
  }

  function format(value, languageCode = 'en', decimals = 1) {
    if (type === 'number') {
      if (typeof value !== 'number' || Number.isNaN(value)) return '-';
      const displayValue = toDisplay(value);
      // Number formatting is always English-locale (consistent decimal/grouping
      // separators); only display names below are localized via languageCode.
      const formattedNumber = displayValue.toLocaleString('en', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      if (!displayUnit) return formattedNumber;
      return displayUnit === '%' ? `${formattedNumber}%` : `${formattedNumber} ${displayUnit}`;
    }
    if (type === 'multiple-choice' && Array.isArray(value)) {
      return value.map((v) => getLabelDisplayName(labelset, v, languageCode)).join(', ');
    }
    if (value == null || value === '') return '';
    return getLabelDisplayName(labelset, value, languageCode);
  }

  return { type, isRatio, rawUnit, displayUnit, toDisplay, toStorage, format };
}

/**
 * Get all labelsets IDs belonging to a specific group
 * @param {Object} labelsets - The labelsets data
 * @param {string} groupId - The id of the group to retrieve the labelsets for
 * @returns {string[]} - The ids of the labelsets belonging to the group
 */
export function getAllLabelsetsBelongingToGroup(labelsets, groupId) {
  if (!labelsets) return [];
  return Object.keys(labelsets).filter((labelsetId) => labelsets[labelsetId]?.group === groupId);
}

/**
 * Get a labelset belonging to a specific group
 * @param {Object} labelsetIds - The ids of the labelsets to search through
 * @param {Object} globalLabelsets - The global labelsets data
 * @param {string} groupId - The id of the group to retrieve the labelset for
 * @returns {string|null} - The labelset-id belonging to the group or null if not found. If multiple labelsets belong to the group, the first one is returned.
 */
export function getLabelsetBelongingToGroup(labelsetIds, globalLabelsets, groupId) {
  if (!labelsetIds || typeof labelsetIds !== 'object') return null;

  for (const labelsetId of labelsetIds) {
    if (!(labelsetId in globalLabelsets)) continue;

    if (globalLabelsets[labelsetId]?.group === groupId) {
      return labelsetId;
    }
  }
  return null;
}

/**
 * From all labelsets belonging to a specific group, get the labelset that contains a specific label
 * @param {string} labelId - The id of the label to search for
 * @param {Object} globalLabelsets - The global labelsets data
 * @param {string} groupId - The id of the group to retrieve the labelset for
 * @returns {string|null} - The labelset-id containing the label or null if not found. If multiple labelsets contain the label, the first one is returned.
 */
export function getLabelsetBelongingToGroupByLabel(labelId, globalLabelsets, groupId) {
  for (const [labelsetId, labelset] of Object.entries(globalLabelsets)) {
    // Check if this labelset belongs to the group
    if (labelset?.group !== groupId) continue;

    // Check if this labelset contains the label
    if (labelId in (labelset?.labels || {})) {
      return labelsetId;
    }
  }
  return null;
}

/**
 * From a dictionary of labelsetId -> labelId, get the labelId that matches the labelset belonging to a specific group
 * @param {Object} labelsetIdToLabelId - The dictionary of labelsetId -> labelId
 * @param {Object} globalLabelsets - The global labelsets data
 * @param {string} groupId - The id of the group to retrieve the label for
 * @returns {string|null} - The labelId that matches the labelset belonging to the group or null if not found. If multiple labelIds match the labelset, the first one is returned.
 */
export function getLabelIdMatchingGroup(labelsetIdToLabelId, globalLabelsets, groupId) {
  const labelsetId = getLabelsetBelongingToGroup(Object.keys(labelsetIdToLabelId || {}), globalLabelsets, groupId);
  if (!labelsetId || !(labelsetId in labelsetIdToLabelId)) return null;
  const labelId = labelsetIdToLabelId[labelsetId];
  return labelId;
}

/**
 * From a dictionary of labelsetId -> labelId, get the display name of the label that matches the labelset belonging to a specific group
 * @param {Object} labelsetIdToLabelId - The dictionary of labelsetId -> labelId
 * @param {Object} globalLabelsets - The global labelsets data
 * @param {string} groupId - The id of the group to retrieve the label for
 * @param {string} languageCode - The language code
 * @returns {string|null} - The display name of the label that matches the labelset belonging to the group or null if not found.
 */
export function getLabelDisplayNameMatchingGroup(labelsetIdToLabelId, globalLabelsets, groupId, languageCode = 'en') {
  const labelsetId = getLabelsetBelongingToGroup(Object.keys(labelsetIdToLabelId || {}), globalLabelsets, groupId);
  if (!labelsetId || !(labelsetId in labelsetIdToLabelId)) return null;
  let labelId = labelsetIdToLabelId[labelsetId];

  // If a whole object is provided, extract the label or class
  if (typeof labelId === 'object') {
    if (labelId.label) {
      labelId = labelId.label;
    } else if (labelId.class) {
      labelId = labelId.class;
    } else {
      return null;
    }
  }
  return getLabelDisplayName(globalLabelsets[labelsetId], labelId, languageCode);
}

/**
 * Get all labeling tasks across all clients
 * @param {Firestore} firestore - The firestore instance
 * @returns {Object} - The labeling tasks data
 */
export async function getLabelingTasksAcrossAllClients(firestore) {
  const labelingTasks = {};
  const labelingTasksRef = collectionGroup(firestore, LABELING_TASKS_COLLECTION_GROUP);
  const labelingTasksSnap = await getDocs(labelingTasksRef);
  labelingTasksSnap.forEach((doc) => {
    labelingTasks[doc.id] = doc.data();
  });
  return labelingTasks;
}

/**
 * Subscribe to labeling tasks across all clients
 * @param {Firestore} firestore - The firestore instance
 * @param {Function} callback - The callback function to call when the labeling tasks change
 * @returns {Function} - The unsubscribe function
 */
export function subscribeToLabelingTasksAcrossAllClients(firestore, callback) {
  const labelingTasksRef = collectionGroup(firestore, LABELING_TASKS_COLLECTION_GROUP);
  const unsubscribe = onSnapshot(labelingTasksRef, (snapshot) => {
    const data = snapshot.docs.reduce((acc, doc) => {
      acc[doc.id] = doc.data();
      return acc;
    }, {});
    callback(data);
  });
  return unsubscribe;
}

/**
 * Get a specific labeling task by its ID
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task to retrieve
 * @returns {Object|null} - The labeling task data or null if not found
 */
export async function getLabelingTaskById(firestore, clientId, labelingTaskId) {
  const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
  const labelingTaskSnap = await getDoc(labelingTaskRef);
  if (labelingTaskSnap.exists()) {
    return {
      id: labelingTaskSnap.id,
      ...labelingTaskSnap.data(),
    };
  } else {
    return null;
  }
}

/**
 * Subscribe to a specific labeling task by its ID
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task to subscribe to
 * @param {Function} callback - The callback function to call when the labeling task changes
 * @returns {Function} - The unsubscribe function
 */
export function subscribeToLabelingTaskById(firestore, clientId, labelingTaskId, callback) {
  const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
  const unsubscribe = onSnapshot(labelingTaskRef, (doc) => {
    if (doc.exists()) {
      callback({
        id: doc.id,
        ...doc.data(),
      });
    } else {
      callback(null);
    }
  });
  return unsubscribe;
}

/**
 * Get all labeling tasks for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @returns {Object} - The labeling tasks data
 */
export async function getLabelingTasksForClient(firestore, clientId) {
  const labelingTasks = {};
  const labelingTasksRef = collection(firestore, LABELING_TASKS_COLLECTION(clientId));
  const labelingTasksSnap = await getDocs(labelingTasksRef);
  labelingTasksSnap.forEach((doc) => {
    labelingTasks[doc.id] = doc.data();
  });
  return labelingTasks;
}

/**
 * Subscribe to all labeling tasks for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {Function} callback - The callback function to call when the labeling tasks change
 * @returns {Function} - The unsubscribe function
 */
export function subscribeToLabelingTasksForClient(firestore, clientId, callback) {
  const labelingTasksRef = collection(firestore, LABELING_TASKS_COLLECTION(clientId));
  const unsubscribe = onSnapshot(labelingTasksRef, (snapshot) => {
    const data = snapshot.docs.reduce((acc, doc) => {
      acc[doc.id] = doc.data();
      return acc;
    }, {});
    callback(data);
  });
  return unsubscribe;
}

/**
 * Get all labeling tasks for a user
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} userId - The id of the user
 * @returns {Object} - The labeling tasks data
 */
export async function getLabelingTasksForUser(firestore, clientId, userId) {
  const labelingTasks = {};
  const labelingTasksRef = query(
    collection(firestore, LABELING_TASKS_COLLECTION(clientId)),
    where('assigned_user_ids', 'array-contains', userId),
  );
  const labelingTasksSnap = await getDocs(labelingTasksRef);
  labelingTasksSnap.forEach((doc) => {
    labelingTasks[doc.id] = doc.data();
  });
  return labelingTasks;
}

/**
 * Subscribe to all labeling tasks for a user
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} userId - The id of the user
 * @param {Function} callback - The callback function to call when the labeling tasks change
 * @returns {Function} - The unsubscribe function
 */
export function subscribeToLabelingTasksForUser(firestore, clientId, userId, callback) {
  const labelingTasksRef = query(
    collection(firestore, LABELING_TASKS_COLLECTION(clientId)),
    where('assigned_user_ids', 'array-contains', userId),
  );
  const unsubscribe = onSnapshot(labelingTasksRef, (snapshot) => {
    const data = snapshot.docs.reduce((acc, doc) => {
      acc[doc.id] = doc.data();
      return acc;
    }, {});
    callback(data);
  });
  return unsubscribe;
}

/**
 * Create a new labeling task for a client
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelsetProfileId - The id of the labelset profile
 * @param {string} taskName - The display name for the task
 * @param {string} description - The description of the task
 * @param {string[]} assignedUserIds - The ids of the users assigned to the task
 * @param {string[]} observationIds - The ids of the observations to label
 * @param {string} createdBy - The id of the user who created the task
 * @param {string} collaborationType - The collaboration type of the task (collaborative or individual)
 * @param {string} status - The status of the task (in_progress, stopped, completed, archived)
 * @param {string|null} guidelinesStoragePath - Storage path of the labeling-guidelines PDF, if any
 * @param {{delivery_id: string, bale_id: string}[]} deliveryBaleRefs - Delivery bales to label (nested under a delivery)
 * @param {boolean} requestAllLabelsets - When true, labelers are warned on Accept if they leave any labelset blank
 */
export async function createLabelingTask(
  firestore,
  clientId,
  labelsetProfileId,
  taskName,
  description = null,
  assignedUserIds = [],
  observationIds = [],
  createdBy = null,
  collaborationType = 'collaborative',
  status = 'in_progress',
  guidelinesStoragePath = null,
  deliveryBaleRefs = [],
  requestAllLabelsets = false,
) {
  // assert collaboration type is either "collaborative" or "individual"
  if (collaborationType !== 'collaborative' && collaborationType !== 'individual') {
    throw new Error("Collaboration type must be either 'collaborative' or 'individual'");
  }

  // Validate status
  if (!LABELING_TASK_STATUSES.includes(status)) {
    throw new Error('Status must be one of the following: ' + LABELING_TASK_STATUSES.join(', '));
  }

  const labelingTaskData = {
    labelset_profile_id: labelsetProfileId,
    client_id: clientId,
    task_name: taskName,
    description: description,
    assigned_user_ids: assignedUserIds,
    observation_ids: observationIds,
    delivery_bale_refs: deliveryBaleRefs,
    created_by: createdBy,
    created_at: serverTimestamp(),
    collaboration_type: collaborationType,
    status: status,
    guidelines_storage_path: guidelinesStoragePath,
    request_all_labelsets: requestAllLabelsets,
  };

  await addDoc(collection(firestore, LABELING_TASKS_COLLECTION(clientId)), labelingTaskData);
}

/**
 * Edit a labeling task. Only the fields that are provided will be updated.
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {Object} options - The update options
 * @param {string} [options.taskName] - The display name for the task
 * @param {string} [options.description] - The description of the task
 * @param {string} [options.collaborationType] - The collaboration type of the task (collaborative or individual)
 * @param {string[]} [options.assignedUserIds] - The ids of the users assigned to the task
 * @param {string[]} [options.observationIds] - The ids of the observations to label
 * @param {{delivery_id: string, bale_id: string}[]} [options.deliveryBaleRefs] - Delivery bales to label
 * @param {string} [options.status] - The status of the task (in_progress, stopped, completed, archived)
 * @param {string} [options.labelsetProfileId] - The id of the labelset profile
 * @param {string|null} [options.guidelinesStoragePath] - Storage path of the labeling-guidelines PDF (null to remove)
 * @param {boolean} [options.requestAllLabelsets] - When true, labelers are warned on Accept if they leave any labelset blank
 */
export async function editLabelingTask(firestore, clientId, labelingTaskId, options = {}) {
  const {
    taskName,
    description,
    collaborationType,
    assignedUserIds,
    observationIds,
    deliveryBaleRefs,
    status,
    labelsetProfileId,
    guidelinesStoragePath,
    requestAllLabelsets,
  } = options;

  // Validate collaboration type if provided
  if (collaborationType !== undefined && collaborationType !== 'collaborative' && collaborationType !== 'individual') {
    throw new Error("Collaboration type must be either 'collaborative' or 'individual'");
  }

  // Validate status if provided
  if (status !== undefined && !LABELING_TASK_STATUSES.includes(status)) {
    throw new Error('Status must be one of the following: ' + LABELING_TASK_STATUSES.join(', '));
  }

  // Build update object with only provided fields
  const updateData = {};
  if (taskName !== undefined) updateData.task_name = taskName;
  if (description !== undefined) updateData.description = description;
  if (collaborationType !== undefined) updateData.collaboration_type = collaborationType;
  if (assignedUserIds !== undefined) updateData.assigned_user_ids = assignedUserIds;
  if (observationIds !== undefined) updateData.observation_ids = observationIds;
  if (deliveryBaleRefs !== undefined) updateData.delivery_bale_refs = deliveryBaleRefs;
  if (status !== undefined) updateData.status = status;
  if (labelsetProfileId !== undefined) updateData.labelset_profile_id = labelsetProfileId;
  if (guidelinesStoragePath !== undefined) updateData.guidelines_storage_path = guidelinesStoragePath;
  if (requestAllLabelsets !== undefined) updateData.request_all_labelsets = requestAllLabelsets;

  // Only update if there are fields to update
  if (Object.keys(updateData).length > 0) {
    const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
    await updateDoc(labelingTaskRef, updateData);
  }
}

/**
 * Add observations to a labeling task
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string[]} observationIds - The ids of the observations to add to the task
 */
export async function addObservationsToLabelingTask(firestore, clientId, labelingTaskId, observationIds) {
  if (!Array.isArray(observationIds) || observationIds.length === 0) {
    throw new Error('observationIds must be a non-empty array');
  }

  const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
  await updateDoc(labelingTaskRef, {
    observation_ids: arrayUnion(...observationIds),
  });
}

/**
 * Remove observations from a labeling task
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string[]} observationIds - The ids of the observations to remove from the task
 */
export async function removeObservationsFromLabelingTask(firestore, clientId, labelingTaskId, observationIds) {
  if (!Array.isArray(observationIds) || observationIds.length === 0) {
    throw new Error('observationIds must be a non-empty array');
  }

  const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
  await updateDoc(labelingTaskRef, {
    observation_ids: arrayRemove(...observationIds),
  });
}

/**
 * Add users to a labeling task
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string[]} userIds - The ids of the users to add to the task
 */
export async function addUsersToLabelingTask(firestore, clientId, labelingTaskId, userIds) {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    throw new Error('userIds must be a non-empty array');
  }

  const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
  await updateDoc(labelingTaskRef, {
    assigned_user_ids: arrayUnion(...userIds),
  });
}

/**
 * Remove users from a labeling task
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string[]} userIds - The ids of the users to remove from the task
 */
export async function removeUsersFromLabelingTask(firestore, clientId, labelingTaskId, userIds) {
  if (!Array.isArray(userIds) || userIds.length === 0) {
    throw new Error('userIds must be a non-empty array');
  }

  const labelingTaskRef = doc(firestore, LABELING_TASKS_COLLECTION(clientId), labelingTaskId);
  await updateDoc(labelingTaskRef, {
    assigned_user_ids: arrayRemove(...userIds),
  });
}

/**
 * Add a label of type "single-choice" or "ordinal" to an observation
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} docId - The id of the observation / bale
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string} labelsetId - The id of the labelset
 * @param {string | null} label - The label class. If null, the label is deleted.
 * @param {string} userId - The id of the user
 */
export async function addSingleChoiceLabelToObservation(
  firestore,
  clientId,
  docId,
  labelsetId,
  label,
  userId,
  labelingTaskId = 'default',
  collectionName = 'observations',
  deliveryId,
) {
  let observationRef = '';
  if (collectionName === 'observations') {
    observationRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), docId);
  } else if (collectionName === 'bales') {
    observationRef = doc(firestore, BALE_DOCUMENT_PATH(clientId, deliveryId, docId));
  }
  await setDoc(
    observationRef,
    {
      labeling_task_data: {
        [labelingTaskId]: {
          labeled_by_users: arrayUnion(userId),
          labels_per_labelset: {
            [labelsetId]: arrayUnion({ label: label, user_id: userId, timestamp: new Date() }),
          },
        },
      },
    },
    { merge: true },
  );
}

/**
 * Add a label of type "multiple-choice" to an observation
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} docId - The id of the observation / bale
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string} labelsetId - The id of the labelset
 * @param {string[] | null} labels - The label classes. If null, the label is deleted.
 * @param {string} userId - The id of the user
 */
export async function addMultipleChoiceLabelToObservation(
  firestore,
  clientId,
  docId,
  labelsetId,
  labels,
  userId,
  labelingTaskId = 'default',
  collectionName = 'observations',
  deliveryId,
) {
  let observationRef = '';
  if (collectionName === 'observations') {
    observationRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), docId);
  } else if (collectionName === 'bales') {
    observationRef = doc(firestore, BALE_DOCUMENT_PATH(clientId, deliveryId, docId));
  }
  await setDoc(
    observationRef,
    {
      labeling_task_data: {
        [labelingTaskId]: {
          labeled_by_users: arrayUnion(userId),
          labels_per_labelset: {
            [labelsetId]: arrayUnion({ label: labels, user_id: userId, timestamp: new Date() }),
          },
        },
      },
    },
    { merge: true },
  );
}

/**
 * Add a label of type "number" or "single-choice-with-number" to an observation
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} docId - The id of the observation / bale
 * @param {string} labelingTaskId - The id of the labeling task
 * @param {string} labelsetId - The id of the labelset
 * @param {number | null} label - The label. If null, the label is deleted.
 * @param {string} userId - The id of the user
 */
export async function addNumberLabelToObservation(
  firestore,
  clientId,
  docId,
  labelsetId,
  label,
  userId,
  labelingTaskId = 'default',
  collectionName = 'observations',
  deliveryId,
) {
  let observationRef = '';
  if (collectionName === 'observations') {
    observationRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), docId);
  } else if (collectionName === 'bales') {
    observationRef = doc(firestore, BALE_DOCUMENT_PATH(clientId, deliveryId, docId));
  }
  await setDoc(
    observationRef,
    {
      labeling_task_data: {
        [labelingTaskId]: {
          labeled_by_users: arrayUnion(userId),
          labels_per_labelset: {
            [labelsetId]: arrayUnion({ label: label, user_id: userId, timestamp: new Date() }),
          },
        },
      },
    },
    { merge: true },
  );
}

/**
 * Remove a user's label for a single labelset within a labeling task.
 *
 * Labels are stored append-only (see `add*LabelToObservation`), and the read path takes, per
 * user, the newest entry by timestamp. Deleting only the latest entry would make the user's
 * previous entry resurface, so this removes *all* of the user's entries for the labelset.
 *
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} docId - The id of the observation / bale
 * @param {string} labelsetId - The id of the labelset to clear for this user
 * @param {string} userId - The id of the user whose label(s) to remove
 * @param {string} [labelingTaskId='default'] - The id of the labeling task
 * @param {string} [collectionName='observations'] - The collection name ('observations' or 'bales')
 * @param {string} [deliveryId] - The delivery id when using the 'bales' collection
 */
export async function removeUserLabelFromObservation(
  firestore,
  clientId,
  docId,
  labelsetId,
  userId,
  labelingTaskId = 'default',
  collectionName = 'observations',
  deliveryId,
) {
  let targetRef = '';
  if (collectionName === 'observations') {
    targetRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), docId);
  } else if (collectionName === 'bales') {
    if (!deliveryId) throw new Error('deliveryId is required when collectionName is "bales"');
    targetRef = doc(firestore, BALE_DOCUMENT_PATH(clientId, deliveryId, docId));
  }

  if (!targetRef) throw new Error(`Unsupported collectionName "${collectionName}"`);

  const snap = await getDoc(targetRef);
  if (!snap.exists()) return;

  const taskData = snap.data()?.labeling_task_data?.[labelingTaskId];
  if (!taskData) return;

  const labelsPerLabelset = taskData.labels_per_labelset ?? {};
  const entries = labelsPerLabelset[labelsetId] ?? [];
  const nextEntries = Array.isArray(entries) ? entries.filter((entry) => entry?.user_id !== userId) : [];

  // Nothing belonged to this user — no-op (mirrors the detection-correction removers).
  if (Array.isArray(entries) && nextEntries.length === entries.length) return;

  // `labeled_by_users` is per-task, so only drop the user when they have no entries left in
  // any labelset of this task (using the post-removal list for the target labelset).
  const userStillHasLabels = Object.entries(labelsPerLabelset).some(([lsId, arr]) => {
    const list = lsId === labelsetId ? nextEntries : (arr ?? []);
    return Array.isArray(list) && list.some((entry) => entry?.user_id === userId);
  });

  const update = {
    [`labeling_task_data.${labelingTaskId}.labels_per_labelset.${labelsetId}`]: nextEntries,
  };
  if (!userStillHasLabels) {
    update[`labeling_task_data.${labelingTaskId}.labeled_by_users`] = arrayRemove(userId);
  }

  await updateDoc(targetRef, update);
}

/**
 * Save a note for a user within a labeling task. Overwrites any previous note by the same user.
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} docId - The id of the observation / bale
 * @param {string} userId - The id of the user writing the note
 * @param {string} noteText - The note text
 * @param {string} [labelingTaskId='default'] - The id of the labeling task
 * @param {string} [collectionName='observations'] - The collection name ('observations' or 'bales')
 * @param {string} [deliveryId] - The delivery id when using the 'bales' collection
 */
export async function saveNoteToLabelingTask(
  firestore,
  clientId,
  docId,
  userId,
  noteText,
  labelingTaskId = 'default',
  collectionName = 'observations',
  deliveryId,
) {
  let targetRef;
  if (collectionName === 'observations') {
    targetRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), docId);
  } else if (collectionName === 'bales') {
    targetRef = doc(firestore, BALE_DOCUMENT_PATH(clientId, deliveryId, docId));
  }
  await setDoc(
    targetRef,
    {
      labeling_task_data: {
        [labelingTaskId]: {
          notes_by_users: {
            [userId]: { text: noteText, timestamp: new Date(), user_id: userId },
          },
        },
      },
    },
    { merge: true },
  );
}

/**
 * Mark observation as labeled by a user for a labeling task
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} observationId - The id of the observation
 * @param {string} userId - The id of the user
 * @param {string} labelingTaskId - The id of the labeling task
 */
export async function setLabelingCompletedForObservation(firestore, clientId, observationId, userId, labelingTaskId) {
  const observationRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), observationId);
  await setDoc(
    observationRef,
    {
      labeling_task_data: {
        [labelingTaskId]: {
          labeled_by_users: arrayUnion(userId),
        },
      },
    },
    { merge: true },
  );
}

/**
 * Add a detection correction of type "false positive" for an observation or bale.
 * This records that an existing detection was removed.
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} docId - The id of the observation / bale
 * @param {string} detectionId - The id of the detection that was marked as false positive
 * @param {string} userId - The id of the user making the correction
 * @param {string} [collectionName='observations'] - The collection name ('observations' or 'bales')
 * @param {string} [deliveryId] - The delivery id when using the 'bales' collection
 */
export async function addFalsePositiveDetectionCorrection(
  firestore,
  clientId,
  docId,
  detectionId,
  userId,
  collectionName = 'observations',
  deliveryId,
) {
  let targetRef = '';
  if (collectionName === 'observations') {
    targetRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), docId);
  } else if (collectionName === 'bales') {
    targetRef = doc(firestore, BALE_DOCUMENT_PATH(clientId, deliveryId, docId));
  }
  await setDoc(
    targetRef,
    {
      detections: {
        detections_corrections: {
          false_positives: arrayUnion({
            id: detectionId,
            uid: userId,
            timestamp: new Date(),
          }),
        },
      },
    },
    { merge: true },
  );
}

/**
 * Remove a detection correction of type "false positive" for an observation or bale.
 * This removes any entries in `detections.detections_corrections.false_positives` matching the provided detectionId.
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} docId - The id of the observation / bale
 * @param {string} detectionId - The id of the detection correction entry to remove
 * @param {string} [collectionName='observations'] - The collection name ('observations' or 'bales')
 * @param {string} [deliveryId] - The delivery id when using the 'bales' collection
 */
export async function removeFalsePositiveDetectionCorrection(
  firestore,
  clientId,
  docId,
  detectionId,
  collectionName = 'observations',
  deliveryId,
) {
  let targetRef = '';
  if (collectionName === 'observations') {
    targetRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), docId);
  } else if (collectionName === 'bales') {
    if (!deliveryId) throw new Error('deliveryId is required when collectionName is "bales"');
    targetRef = doc(firestore, BALE_DOCUMENT_PATH(clientId, deliveryId, docId));
  }

  if (!targetRef) throw new Error(`Unsupported collectionName "${collectionName}"`);

  const snap = await getDoc(targetRef);
  if (!snap.exists()) return;

  const falsePositives = snap.data()?.detections?.detections_corrections?.false_positives || [];
  const nextFalsePositives = Array.isArray(falsePositives)
    ? falsePositives.filter((entry) => entry?.id !== detectionId)
    : [];

  if (Array.isArray(falsePositives) && nextFalsePositives.length === falsePositives.length) return;

  await updateDoc(targetRef, {
    'detections.detections_corrections.false_positives': nextFalsePositives,
  });
}

/**
 * Add a detection correction of type "false negative" for an observation or bale.
 * This records that a missing detection was added by a user.
 * Stored under `detections.detections_corrections.false_negatives`.
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} docId - The id of the observation / bale
 * @param {string} labelsetId - The labelset id (e.g. fiber_contaminants)
 * @param {string} cls - The label id/class (e.g. plastic_foil)
 * @param {string} userId - The id of the user making the correction
 * @param {string} [collectionName='observations'] - The collection name ('observations' or 'bales')
 * @param {string} [deliveryId] - The delivery id when using the 'bales' collection
 */
export async function addFalseNegativeDetectionCorrection(
  firestore,
  clientId,
  docId,
  labelsetId,
  cls,
  userId,
  collectionName = 'observations',
  deliveryId,
) {
  let targetRef = '';
  if (collectionName === 'observations') {
    targetRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), docId);
  } else if (collectionName === 'bales') {
    targetRef = doc(firestore, BALE_DOCUMENT_PATH(clientId, deliveryId, docId));
  }

  if (!targetRef) throw new Error(`Unsupported collectionName "${collectionName}"`);

  await setDoc(
    targetRef,
    {
      detections: {
        detections_corrections: {
          false_negatives: arrayUnion({
            labelset: labelsetId,
            cls,
            uid: userId,
            timestamp: new Date(),
          }),
        },
      },
    },
    { merge: true },
  );
}

/**
 * Remove a detection correction of type "false negative" for an observation or bale.
 * Removes entries in `detections.detections_corrections.false_negatives` matching labelset+cls+uid.
 * @param {Firestore} firestore - The firestore instance
 * @param {string} clientId - The id of the client
 * @param {string} docId - The id of the observation / bale
 * @param {string} labelsetId - The labelset id
 * @param {string} cls - The label id/class
 * @param {string} userId - The id of the user that created the correction
 * @param {string} [collectionName='observations'] - The collection name ('observations' or 'bales')
 * @param {string} [deliveryId] - The delivery id when using the 'bales' collection
 */
export async function removeFalseNegativeDetectionCorrection(
  firestore,
  clientId,
  docId,
  labelsetId,
  cls,
  userId,
  collectionName = 'observations',
  deliveryId,
) {
  let targetRef = '';
  if (collectionName === 'observations') {
    targetRef = doc(firestore, OBSERVATIONS_COLLECTION(clientId), docId);
  } else if (collectionName === 'bales') {
    if (!deliveryId) throw new Error('deliveryId is required when collectionName is "bales"');
    targetRef = doc(firestore, BALE_DOCUMENT_PATH(clientId, deliveryId, docId));
  }

  if (!targetRef) throw new Error(`Unsupported collectionName "${collectionName}"`);

  const snap = await getDoc(targetRef);
  if (!snap.exists()) return;

  const falseNegatives = snap.data()?.detections?.detections_corrections?.false_negatives || [];
  const nextFalseNegatives = Array.isArray(falseNegatives)
    ? falseNegatives.filter((entry) => !(entry?.labelset === labelsetId && entry?.cls === cls && entry?.uid === userId))
    : [];

  if (Array.isArray(falseNegatives) && nextFalseNegatives.length === falseNegatives.length) return;

  await updateDoc(targetRef, {
    'detections.detections_corrections.false_negatives': nextFalseNegatives,
  });
}
